import React, { useEffect, useMemo, useState } from "react";
import { copyText } from "@/lib/clipboard";
import { QRCodeCanvas } from "qrcode.react";
import { useQuery } from "@tanstack/react-query";
import { Copy, RefreshCw, ShieldCheck, Clock, CheckCircle2, XCircle, AlertTriangle, Beaker } from "lucide-react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { useBillingActions } from "@/hooks/useBilling";
import { SubStatusBadge } from "@/components/settings/SubStatusBadge";
import { FormDrawer } from "@/components/kit/FormDrawer";
import { TextField } from "@/components/kit/fields";
import { Button } from "@/components/ui/button";
import { fmtMoney } from "@/lib/format";
import { cn } from "@/lib/utils";

function useCountdown(expiresAt) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  const ms = expiresAt ? new Date(expiresAt).getTime() - now : 0;
  const total = Math.max(0, Math.floor(ms / 1000));
  return { mm: String(Math.floor(total / 60)).padStart(2, "0"), ss: String(total % 60).padStart(2, "0"), expired: total <= 0 };
}

const SCENARIOS = [
  { key: "match", label: "Match (paid)" },
  { key: "none", label: "Not found" },
  { key: "duplicate", label: "Duplicate" },
  { key: "expired", label: "Expired" },
  { key: "failed", label: "API error" },
];

export function CryptoCheckout({ open, orderId, onOpenChange }) {
  const { checkOrder, submitTx, simulate } = useBillingActions();
  const [txHash, setTxHash] = useState("");
  const { data: order, refetch } = useQuery({
    queryKey: ["order", orderId],
    queryFn: async () => (await http.get(`/billing/orders/${orderId}`)).data,
    enabled: !!orderId && open,
  });
  useEffect(() => { if (open) setTxHash(""); }, [open, orderId]);

  const cd = useCountdown(order?.expires_at);
  const status = order?.status;
  const isMock = order?.provider_type === "mock";
  const finalized = status === "paid" || status === "rejected" || status === "expired";

  const copy = (v) => copyText(v);

  const check = async () => {
    const res = await checkOrder.mutateAsync(orderId);
    const d = res.data;
    refetch();
    if (d.status === "paid") toast.success("Payment confirmed — subscription extended");
    else if (d.status === "needs_tx_hash") toast.warning(d.message);
    else if (d.status === "expired") toast.error("Payment order expired");
    else if (d.status === "failed") toast.error(d.message);
    else toast.info(d.message);
  };
  const submit = async () => {
    if (!txHash.trim()) return toast.error("Enter your TX hash");
    try {
      const res = await submitTx.mutateAsync({ id: orderId, tx_hash: txHash.trim() });
      refetch();
      const d = res.data;
      if (d.status === "paid") toast.success("Payment confirmed");
      else toast.warning(d.message);
    } catch (e) { toast.error(e.response?.data?.detail || "Verification failed"); }
  };
  const sim = async (scenario) => {
    await simulate.mutateAsync({ id: orderId, scenario });
    refetch();
    toast.success(`Simulated: ${scenario}`);
  };

  if (!orderId) return null;

  return (
    <FormDrawer open={open} onOpenChange={onOpenChange} title="Crypto checkout"
      subtitle={order ? `${order.plan_name} subscription` : "Loading…"} size="lg" hideFooter testId="crypto-checkout">
      {!order ? <div className="text-sm text-muted-foreground">Loading order…</div> : (
        <div className="space-y-5" data-testid="checkout-body">
          {/* Amount hero */}
          <div className="rounded-2xl border border-white/[0.08] bg-[#0F0F11] p-6 text-center">
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Amount due</div>
            <div className="mt-1 font-display text-4xl font-bold tracking-tight text-foreground">{fmtMoney(order.amount, order.currency)}</div>
            <div className="mt-1 text-sm text-muted-foreground">{order.network} · {order.currency}</div>
          </div>

          {/* Status + timer */}
          <div className="flex items-center justify-between rounded-xl border border-white/[0.08] bg-[#141417] px-4 py-3">
            <div className="flex items-center gap-2"><span className="text-xs text-muted-foreground">Status</span><SubStatusBadge status={status} testId="checkout-status" /></div>
            <div className={cn("flex items-center gap-1.5 font-mono text-sm", cd.expired ? "text-[#EF4444]" : "text-foreground")} data-testid="checkout-timer">
              <Clock className="h-4 w-4" /> {cd.expired ? "Expired" : `${cd.mm}:${cd.ss}`}
            </div>
          </div>

          {status === "paid" ? (
            <div className="flex flex-col items-center gap-2 rounded-xl border border-[#22C55E]/30 bg-[#22C55E]/[0.06] py-8 text-center" data-testid="checkout-paid">
              <CheckCircle2 className="h-9 w-9 text-[#22C55E]" />
              <p className="text-sm font-medium text-foreground">Payment confirmed</p>
              <p className="text-xs text-muted-foreground">Subscription extended. TX: {order.tx_hash}</p>
              <Button onClick={() => onOpenChange(false)} className="mt-2 bg-white text-black hover:bg-white/90">Done</Button>
            </div>
          ) : status === "rejected" ? (
            <div className="flex flex-col items-center gap-2 rounded-xl border border-[#EF4444]/30 bg-[#EF4444]/[0.06] py-8 text-center" data-testid="checkout-rejected">
              <XCircle className="h-9 w-9 text-[#EF4444]" /><p className="text-sm font-medium text-foreground">Order rejected</p>
              <p className="text-xs text-muted-foreground">{order.fail_reason}</p>
            </div>
          ) : (
            <>
              {/* QR + address */}
              <div className="flex flex-col items-center gap-4 rounded-xl border border-white/[0.08] bg-[#141417] p-6 sm:flex-row sm:items-start">
                <div className="rounded-xl bg-white p-3" data-testid="checkout-qr">
                  <QRCodeCanvas value={order.wallet_address} size={132} bgColor="#ffffff" fgColor="#0A0A0A" />
                </div>
                <div className="min-w-0 flex-1 space-y-2">
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">Send {order.currency} to this address</div>
                  <div className="flex items-center gap-2 rounded-lg border border-white/[0.08] bg-[#0A0A0A] px-3 py-2.5">
                    <code className="flex-1 break-all font-mono text-xs text-foreground" data-testid="checkout-address">{order.wallet_address}</code>
                    <button onClick={() => copy(order.wallet_address)} className="shrink-0 text-muted-foreground hover:text-foreground"><Copy className="h-4 w-4" /></button>
                  </div>
                  <p className="text-xs text-muted-foreground">Send the exact amount on the {order.network} network only. Wrong network = lost funds.</p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-2">
                <Button onClick={check} disabled={checkOrder.isPending || cd.expired} data-testid="checkout-check-btn"
                  className="flex-1 gap-2 bg-white text-black hover:bg-white/90">
                  <ShieldCheck className="h-4 w-4" /> I paid — check automatically
                </Button>
                <Button onClick={() => { refetch(); check(); }} variant="outline" data-testid="checkout-refresh-btn"
                  className="gap-2 border-white/15"><RefreshCw className="h-4 w-4" /> Refresh status</Button>
              </div>

              {/* TX hash (needed when disputed / not found) */}
              {(status === "needs_tx_hash" || status === "pending" || status === "failed") && (
                <div className={cn("space-y-3 rounded-xl border p-4",
                  status === "needs_tx_hash" ? "border-[#EAB308]/30 bg-[#EAB308]/[0.05]" : "border-white/[0.08] bg-[#141417]")}
                  data-testid="checkout-txhash-block">
                  {status === "needs_tx_hash" && (
                    <div className="flex items-start gap-2 text-sm text-[#EAB308]"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                      <span>Multiple payments of the same amount were found. Enter your TX hash so we can identify yours.</span></div>
                  )}
                  <TextField label="TX Hash (optional manual confirmation)" value={txHash} onChange={setTxHash} testId="checkout-txhash-input" placeholder="0x… / mock-…" />
                  <Button onClick={submit} disabled={submitTx.isPending} data-testid="checkout-submit-tx-btn" className="bg-white text-black hover:bg-white/90">Submit TX hash</Button>
                </div>
              )}

              {/* Mock simulation controls */}
              {isMock && !finalized && (
                <div className="rounded-xl border border-dashed border-white/[0.12] bg-white/[0.02] p-4" data-testid="checkout-simulate">
                  <div className="mb-2 flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground"><Beaker className="h-3.5 w-3.5" /> Mock provider — simulate incoming payment</div>
                  <div className="flex flex-wrap gap-2">
                    {SCENARIOS.map((s) => (
                      <Button key={s.key} onClick={() => sim(s.key)} variant="outline" data-testid={`simulate-${s.key}`} className="h-8 border-white/15 text-xs">{s.label}</Button>
                    ))}
                  </div>
                  <p className="mt-2 text-[11px] text-muted-foreground">After simulating, press “check automatically”. This control disappears with a real Tron provider.</p>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </FormDrawer>
  );
}
